import {addUrlToHistoryStack} from '@github-ui/history'
import type {DashboardIssueIdentifier} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {type ReactNode, type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {DashboardIssueSidePanel} from '../components/DashboardIssueSidePanel'

// The open side-panel issue is mirrored to `?issue=owner|repo|number` so it's shareable and
// browser back/forward opens/closes the panel, matching the issues dashboard.
const SIDE_PANEL_ISSUE_PARAM = 'issue'
const SIDE_PANEL_GLOBAL_ID_PARAM = 'issue_global_id'

// The parts are interpolated into `/:owner/:repo/issues/:number` URLs and Relay variables, so a
// hand-edited param must not be able to smuggle in path traversal or other URL-special characters.
const NAME_SEGMENT = /^(?!\.{1,2}$)[\w.-]+$/

function parseSidePanelIssueFromUrl(): DashboardIssueIdentifier | null {
  const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
  const reference = url.searchParams.get(SIDE_PANEL_ISSUE_PARAM)
  if (!reference) return null

  const [owner, repo, rawNumber, ...rest] = reference.split('|')
  if (rest.length > 0 || !owner || !repo || !rawNumber) return null
  if (!NAME_SEGMENT.test(owner) || !NAME_SEGMENT.test(repo) || !/^\d+$/.test(rawNumber)) return null

  const number = Number(rawNumber)
  if (!Number.isSafeInteger(number) || number <= 0) return null
  const globalId = url.searchParams.get(SIDE_PANEL_GLOBAL_ID_PARAM) || undefined

  return {owner, repo, number, globalId}
}

function writeSidePanelIssueToUrl(identifier: DashboardIssueIdentifier | null): void {
  const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
  if (identifier) {
    url.searchParams.set(SIDE_PANEL_ISSUE_PARAM, `${identifier.owner}|${identifier.repo}|${identifier.number}`)
    if (identifier.globalId) {
      url.searchParams.set(SIDE_PANEL_GLOBAL_ID_PARAM, identifier.globalId)
    } else {
      url.searchParams.delete(SIDE_PANEL_GLOBAL_ID_PARAM)
    }
  } else {
    url.searchParams.delete(SIDE_PANEL_ISSUE_PARAM)
    url.searchParams.delete(SIDE_PANEL_GLOBAL_ID_PARAM)
  }
  addUrlToHistoryStack(url.toString())
}

interface UseDashboardIssueSidePanelParams {
  /** Navigable issues in list order for j/k navigation. PRs are excluded so nav skips them. */
  navigableIssues: DashboardIssueIdentifier[]
  /** Refetches the list so panel edits/deletes reflect in it. */
  refreshList: () => void
}

/**
 * Spread onto the dashboard list container so it can receive focus after the open row is deleted.
 */
export type DashboardListContainerProps = {
  ref?: RefObject<HTMLDivElement | null>
  tabIndex?: number
  role?: 'region'
  'aria-label'?: string
}

interface UseDashboardIssueSidePanelResult {
  /** Row-click handler for `IssuesList`. */
  onIssueSelect: (identifier: DashboardIssueIdentifier, returnFocusElement: HTMLElement) => void
  /**
   * True while the panel is showing an issue. The panel binds `j`/`k` to step between issues, so
   * the list below must stop handling those keys for as long as this is true.
   */
  isSidePanelOpen: boolean
  /** The issue currently open in the panel, retained across list page/filter changes. */
  sidePanelIssue: DashboardIssueIdentifier | null
  /** Makes the list container a labelled focus target for when the open row is deleted. */
  listContainerProps: DashboardListContainerProps
  /** The rendered side panel. */
  sidePanel: ReactNode
}

/**
 * Owns the dashboard issue side-panel behavior: open state, `?issue=` URL sync (shareable +
 * back/forward), j/k navigation between issues, opening sub-issues in place, and the focus/refresh
 * choreography around edits and deletes.
 *
 * Must be called from the route component, not the list content component: the latter is unmounted
 * both by the navigation skeleton and by the list query's suspense, so panel state kept there
 * would be lost on every refresh.
 */
export function useDashboardIssueSidePanel({
  navigableIssues,
  refreshList,
}: UseDashboardIssueSidePanelParams): UseDashboardIssueSidePanelResult {
  const [sidePanelIssue, setSidePanelIssue] = useState<DashboardIssueIdentifier | null>(null)
  // Restores focus to the clicked row link when the panel closes normally.
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const listContainerRef = useRef<HTMLDivElement | null>(null)
  const focusListOnCloseRef = useRef(false)

  // Set the open issue and mirror it to the URL (?issue=owner|repo|number) so the panel is
  // shareable and browser back/forward opens/closes it, matching the issues dashboard.
  const commitSidePanelIssue = useCallback((identifier: DashboardIssueIdentifier | null) => {
    setSidePanelIssue(identifier)
    writeSidePanelIssueToUrl(identifier)
  }, [])

  const handleIssueSelect = useCallback(
    (identifier: DashboardIssueIdentifier, returnFocusElement: HTMLElement) => {
      // Capture the actual clicked anchor (not document.activeElement, which mouse clicks
      // don't reliably update in Safari/WebKit) so focus returns deterministically on close.
      returnFocusRef.current = returnFocusElement
      commitSidePanelIssue(identifier)
    },
    [commitSidePanelIssue],
  )

  const navigateToAdjacentIssue = useCallback(
    (delta: 1 | -1) => {
      if (!sidePanelIssue) return
      const currentIndex = navigableIssues.findIndex(
        item =>
          item.number === sidePanelIssue.number &&
          item.owner === sidePanelIssue.owner &&
          item.repo === sidePanelIssue.repo,
      )
      if (currentIndex < 0) return
      const target = navigableIssues[currentIndex + delta]
      if (target) commitSidePanelIssue(target)
    },
    [commitSidePanelIssue, navigableIssues, sidePanelIssue],
  )

  const handleNavigateToNextItem = useCallback(() => navigateToAdjacentIssue(1), [navigateToAdjacentIssue])
  const handleNavigateToPreviousItem = useCallback(() => navigateToAdjacentIssue(-1), [navigateToAdjacentIssue])

  const handleSubIssueSelect = useCallback(
    (identifier: DashboardIssueIdentifier) => {
      // Open the clicked sub-issue in the same panel. The originally clicked row stays the
      // focus-return target, matching the issues dashboard.
      commitSidePanelIssue(identifier)
    },
    [commitSidePanelIssue],
  )

  // An edit shouldn't refetch the list underneath the open panel: it would cost one request per
  // change for a result the user can't see yet. Record that a refresh is pending and run it once
  // on close. Deletion is the exception (the row must go), so it refreshes immediately.
  const pendingRefreshRef = useRef(false)
  const handleIssueChange = useCallback(() => {
    pendingRefreshRef.current = true
  }, [])

  const consumePendingRefresh = useCallback(() => {
    if (!pendingRefreshRef.current) return
    pendingRefreshRef.current = false
    refreshList()
  }, [refreshList])

  // Read from the popstate subscription below, which stays mounted across renders.
  const consumePendingRefreshRef = useRef(consumePendingRefresh)
  useEffect(() => {
    consumePendingRefreshRef.current = consumePendingRefresh
  }, [consumePendingRefresh])

  const handleSidePanelClose = useCallback(() => {
    commitSidePanelIssue(null)
    consumePendingRefresh()
  }, [commitSidePanelIssue, consumePendingRefresh])

  const handleIssueDelete = useCallback(() => {
    // Primer captures its return-focus target when the panel opens, so it will try to restore
    // focus to the row this delete is about to remove. Focusing a detached node is a no-op, which
    // would drop focus to document.body — flag the list container to take focus instead.
    focusListOnCloseRef.current = true
    // This refresh covers any edits made earlier in the same session, so drop the pending flag
    // to keep the close from firing a second one.
    pendingRefreshRef.current = false
    refreshList()
    handleSidePanelClose()
  }, [refreshList, handleSidePanelClose])

  // Runs once the panel has closed (and unmounted, so its own focus restore has already run).
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- the delete handler can't do this itself: focus must be claimed after the panel unmounts and restores focus to the removed row
    if (sidePanelIssue || !focusListOnCloseRef.current) return
    focusListOnCloseRef.current = false
    listContainerRef.current?.focus()
  }, [sidePanelIssue])

  // Open the panel from the URL on mount (client-only, so SSR renders it closed) and keep it in
  // sync with browser back/forward. This intentionally uses an effect:
  // it hydrates initial state from the URL on mount and subscribes to the history `popstate`
  // store.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-initialize-state, react-you-might-not-need-an-effect/no-external-store-subscription -- hydrate initial open issue from the URL on mount and follow popstate
    setSidePanelIssue(parseSidePanelIssueFromUrl())

    const onPopState = () => {
      const identifier = parseSidePanelIssueFromUrl()
      setSidePanelIssue(identifier)
      // Backing out of an edited issue closes the panel without going through the close handler,
      // so run the deferred refresh here. The URL already reflects the state history restored,
      // so this must not write another entry.
      if (!identifier) consumePendingRefreshRef.current()
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const listContainerProps = useMemo<DashboardListContainerProps>(
    () => ({ref: listContainerRef, tabIndex: -1, role: 'region', 'aria-label': 'Issues'}),
    [],
  )

  return {
    onIssueSelect: handleIssueSelect,
    isSidePanelOpen: sidePanelIssue !== null,
    sidePanelIssue,
    listContainerProps,
    sidePanel: (
      <DashboardIssueSidePanel
        issue={sidePanelIssue}
        onClose={handleSidePanelClose}
        onIssueChange={handleIssueChange}
        onIssueDelete={handleIssueDelete}
        onNavigateToNextItem={handleNavigateToNextItem}
        onNavigateToPreviousItem={handleNavigateToPreviousItem}
        onSubIssueSelect={handleSubIssueSelect}
        returnFocusRef={returnFocusRef}
      />
    ),
  }
}
